import React, { useEffect, useState } from 'react';

import { connect } from 'react-redux';
import { AnyAction } from 'redux';
import { ThunkDispatch } from 'redux-thunk';

import {
  EMPLOYEE_STATUS,
  EmployeeStatus,
  loadOrgEmployeesByStatus,
} from '../employees/actions/load-org-employees-by-status';
import { employeeSelector, employeesForOrgIdSelector } from '../reducers/employee-selectors';

import { UiDialog } from '../ui/ui-dialog';

import { EmployeeMergeConfirmation } from './employee-merge-confirmation';
import { EmployeeMergeResolver } from './employee-merge-resolver';

type InputProps = {
  employeeToKeepId: number;
  orgId: number;
  close: () => void;
};

const mapStateToProps = (state: any, { employeeToKeepId, orgId }: InputProps) => ({
  employees: employeesForOrgIdSelector(state, orgId),
  employeeToKeep: employeeSelector(state, employeeToKeepId),
});

const mapDispatchToProps = (
  dispatch: ThunkDispatch<any, any, AnyAction>,
  { orgId }: InputProps,
) => ({
  actions: {
    loadOrgEmployees: (employeeStatus: EmployeeStatus) =>
      dispatch(loadOrgEmployeesByStatus(orgId, employeeStatus)),
  },
});

type ReduxProps = ReturnType<typeof mapStateToProps>;
type DispatchProps = ReturnType<typeof mapDispatchToProps>;
type Props = InputProps & ReduxProps & DispatchProps;

export const EmployeeMergeWizard = connect(
  mapStateToProps,
  mapDispatchToProps,
)(({ orgId, employeeToKeep, employees, close, actions }: Props) => {
  const nonSelectedEmployees = employees.filter(
    (employee) => employee.employeeId !== employeeToKeep.employeeId,
  );
  // See if any other employees have same first and last name, or same email and phone number, and pre-fill selection with that person
  const matchingContactEmployeeId = nonSelectedEmployees.find(
    (employee) =>
      (employee.Contact.Person.firstName === employeeToKeep.Contact.Person.firstName &&
        employee.Contact.Person.lastName === employeeToKeep.Contact.Person.lastName) ||
      (employeeToKeep.Contact.Person.email &&
        employee.Contact.Person.email === employeeToKeep.Contact.Person.email &&
        employeeToKeep.Contact.Person.phoneNumber &&
        employee.Contact.Person.phoneNumber === employeeToKeep.Contact.Person.phoneNumber),
  )?.employeeId;

  const [currentStep, setCurrentStep] = useState<number>(0);
  const [employeeToRemoveId, setEmployeeIdToKeep] = useState<number | null>(
    matchingContactEmployeeId || null,
  );
  const [mergeResolution, setMergeResolution] = useState<
    Record<keyof EmployeeMergeResolutionType, string>
  >({} as Record<keyof EmployeeMergeResolutionType, string>);

  const employeeToRemove = employees.find((e) => e.employeeId === employeeToRemoveId);

  const mergeSteps = [EmployeeMergeResolver, EmployeeMergeConfirmation];

  const nextStep = () => {
    setCurrentStep(currentStep + 1);
  };

  const previousStep = () => {
    setCurrentStep(currentStep - 1);
  };

  const CurrentComponent = mergeSteps[currentStep];

  useEffect(() => {
    actions.loadOrgEmployees(EMPLOYEE_STATUS.CURRENT);
    actions.loadOrgEmployees(EMPLOYEE_STATUS.FORMER);
  }, [orgId, actions]);

  return (
    <UiDialog onClose={close} title="Combine Employees" size="sm">
      <CurrentComponent
        employeeToKeep={employeeToKeep}
        setEmployeeIdToKeep={setEmployeeIdToKeep}
        employeeToRemove={employeeToRemove}
        employees={nonSelectedEmployees}
        mergeResolution={mergeResolution}
        setMergeResolution={setMergeResolution}
        previousStep={previousStep}
        nextStep={nextStep}
        close={close}
      />
    </UiDialog>
  );
});
